import React, { useState, useMemo } from 'react'
import { useReactFlow } from '@xyflow/react'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

interface NodeDebugPanelProps {
  nodeId: string
  state: Record<string, unknown>
  onClose: () => void
}

/** Collapsible JSON tree node */
function JsonNode({ label, value, depth = 0 }: { label: string; value: unknown; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 1)

  if (value === null || value === undefined) {
    return (
      <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-orange-400">{String(value)}</span>
      </div>
    )
  }

  if (typeof value === 'boolean') {
    return (
      <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-yellow-400">{String(value)}</span>
      </div>
    )
  }

  if (typeof value === 'number') {
    return (
      <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-blue-400">{value}</span>
      </div>
    )
  }

  if (typeof value === 'string') {
    const display = value.length > 120 ? value.slice(0, 120) + '…' : value
    return (
      <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-green-400 break-all">&quot;{display}&quot;</span>
      </div>
    )
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
          <span className="text-muted-foreground">{label}:</span>
          <span className="text-muted-foreground/60">[]</span>
        </div>
      )
    }
    return (
      <div style={{ paddingLeft: depth * 12 }}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-0.5 text-muted-foreground hover:text-foreground"
        >
          <Icon name={expanded ? 'chevron-down' : 'chevron-right'} className="h-3 w-3" />
          {label} <span className="text-muted-foreground/60">[{value.length}]</span>
        </button>
        {expanded && value.map((item, i) => (
          <JsonNode key={i} label={String(i)} value={item} depth={depth + 1} />
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) {
      return (
        <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
          <span className="text-muted-foreground">{label}:</span>
          <span className="text-muted-foreground/60">{'{}'}</span>
        </div>
      )
    }
    return (
      <div style={{ paddingLeft: depth * 12 }}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-0.5 text-muted-foreground hover:text-foreground"
        >
          <Icon name={expanded ? 'chevron-down' : 'chevron-right'} className="h-3 w-3" />
          {label} <span className="text-muted-foreground/60">{`{${entries.length}}`}</span>
        </button>
        {expanded && entries.map(([k, v]) => (
          <JsonNode key={k} label={k} value={v} depth={depth + 1} />
        ))}
      </div>
    )
  }

  return (
    <div className="flex items-baseline gap-1" style={{ paddingLeft: depth * 12 }}>
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground">{String(value)}</span>
    </div>
  )
}

export const NodeDebugPanel: React.FC<NodeDebugPanelProps> = ({ nodeId, state, onClose }) => {
  const [tab, setTab] = useState<'state' | 'reactflow'>('state')
  const reactFlow = useReactFlow()

  const rfNode = useMemo(() => {
    const node = reactFlow.getNode(nodeId)
    if (!node) return null
    return {
      id: node.id,
      type: node.type,
      position: node.position,
      measured: node.measured,
      selected: node.selected,
      dragging: node.dragging,
      parentId: node.parentId,
    }
  }, [reactFlow, nodeId])

  const edges = useMemo(() => {
    return reactFlow.getEdges().filter(e => e.source === nodeId || e.target === nodeId).map(e => ({
      id: e.id,
      source: e.source,
      sourceHandle: e.sourceHandle,
      target: e.target,
      targetHandle: e.targetHandle,
    }))
  }, [reactFlow, nodeId])

  return (
    <div className="mt-1 rounded-b-[10px] border-t border-border/30 bg-black/80 text-xs font-mono backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-border/20">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Debug</span>
          <span className="text-[10px] text-muted-foreground">{nodeId}</span>
        </div>
        <div className="flex items-center gap-1">
          <CopyButton data={tab === 'state' ? state : { node: rfNode, edges }} />
          <button onClick={onClose} className="p-0.5 hover:text-foreground text-muted-foreground">
            <Icon name="x" className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 px-2 pt-1">
        <button
          onClick={() => setTab('state')}
          className={cn('px-2 py-0.5 rounded text-[10px]', tab === 'state' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground')}
        >
          State
        </button>
        <button
          onClick={() => setTab('reactflow')}
          className={cn('px-2 py-0.5 rounded text-[10px]', tab === 'reactflow' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground')}
        >
          React Flow
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[300px] overflow-auto p-2 space-y-0.5 text-[11px] leading-[16px]">
        {tab === 'state' && (
          <>
            {Object.entries(state).map(([key, value]) => (
              <JsonNode key={key} label={key} value={value} />
            ))}
            {Object.keys(state).length === 0 && (
              <span className="text-muted-foreground/60">Empty state</span>
            )}
          </>
        )}
        {tab === 'reactflow' && (
          <>
            <JsonNode label="node" value={rfNode} />
            <JsonNode label="edges" value={edges} />
          </>
        )}
      </div>
    </div>
  )
}

/** Small copy-to-clipboard button */
function CopyButton({ data }: { data: unknown }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button onClick={handleCopy} className="p-0.5 hover:text-foreground text-muted-foreground" title="Copy JSON">
      <Icon name={copied ? 'check' : 'clipboard'} className="h-3 w-3" />
    </button>
  )
}
